import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { parse } from 'query-string';
import classNames from 'classnames/bind';

import {
  DataBlocks,
  I18n,
  ErrorBlock,
  RouterLink,
  Button,
  Heading,
  Preloader,
} from 'components';

import { formatDate } from 'utils/formatString';

import {
  fetchInvoiceDetalizationInitialData,
  toggleExpensesFilter,
  saveDetalization,
  stopDownloading,
} from '../../actions';

import { EXPENSES_SECTIONS } from '../../constants';

import ExpensesTabs from '../../components/ExpensesTabs';
import CtnInfoTable from '../../components/CtnInfoTable';
import TotalSpent from '../../components/TotalSpent';
import SaveDetalizationPopup from '../../components/SaveDetalizationPopup';
import SaveDetalization from '../../components/SaveDetalization';

import styles from './styles.pcss';

const cx = classNames.bind(styles);

class InvoiceDetalization extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      sendToEmail: true,
      includeStructure: true,
      includeDetalization: true,
      isPopupOpened: false,
      activeTab: EXPENSES_SECTIONS.STRUCTURE,
    };
  }

  componentDidMount() {
    const {
      actions,
      location,
    } = this.props;

    actions.fetchInvoiceDetalizationInitialData(parse(location.search));
  }

  setActiveTab = (tab) => {
    this.setState({ activeTab: tab });
  };

  render() {
    const {
      transactions,
      error,
      structure,
      actions,
      filters,
      location,
      startDate,
      endDate,
      benNumber,
      invoiceNumber,
      amount,
      status,
      currency,
      dueDate,
      unpaidAmount,
      vat,
      penalties,
      discountParams,
      discountValue,
      isEmailConfirmed,
      isReportSending,
      reportSendingError,
      tooManyTransactions,
      paymentUrl,
      isFetching,
    } = this.props;

    const {
      sendToEmail,
      includeStructure,
      includeDetalization,
      activeTab,
      isPopupOpened,
    } = this.state;

    if (error) {
      return (
        <ErrorBlock
          title={<I18n path="detalization.blockTitle" />}
          refreshAction={() => actions.fetchInvoiceDetalizationInitialData(parse(location.search))}
        />
      );
    }

    return (
      <DataBlocks.Container>
        <DataBlocks.Block
          isLoading={isFetching}
          title={<I18n path="detalization.blockTitle" />}
        >
          <RouterLink
            className="link--hidden-visited-color"
            to="/detalization"
            exact
          >
            <I18n path="detalization.back" />
          </RouterLink>
          <DataBlocks.ContentGroup
            title={(
              <I18n
                path="detalization.postpaidInvoices.invoice.title"
                vars={{ start: formatDate(startDate), end: formatDate(endDate) }}
              />
            )}
            info={(
              <Button
                className="default"
                onClick={() => this.setState({
                  isPopupOpened: true,
                })}
              >
                <I18n path="detalization.postpaidInvoices.invoice.saveReport" />
              </Button>
            )}
          >
            <CtnInfoTable
              data={[{
                benNumber,
                invoiceNumber,
                amount,
                status,
                currency,
                dueDate,
                unpaidAmount,
                paymentUrl,
              }]}
            />
            {tooManyTransactions ? (
              <>
                {isReportSending ? (
                  <Preloader className={cx('preloader')} size="80" />
                ) : (
                  <div className={cx('tooManyTransactionsMessage')}>
                    <Heading className={cx('tooManyTransactionsTitle')} level={4}>
                      <I18n path="detalization.expenses.tooManyTransactionsTitle" />
                    </Heading>
                    <SaveDetalization
                      showSavingOptions
                      includeStructure={includeStructure}
                      includeDetalization={includeDetalization}
                      sendToEmail={sendToEmail}
                      isEmailConfirmed={isEmailConfirmed}
                      toggleIncludeStructure={() => this.setState({
                        includeStructure: !includeStructure,
                      })}
                      toggleIncludeDetalization={() => this.setState({
                        includeDetalization: !includeDetalization,
                      })}
                      toggleSendToEmail={() => this.setState({
                        sendToEmail: !sendToEmail,
                      })}
                      saveReport={() => actions.saveDetalization({
                        ben: benNumber,
                        billDate: endDate,
                        isSendToEmail: isEmailConfirmed && sendToEmail,
                        includeCallDetail: includeDetalization,
                        includeCharges: includeStructure,
                      }, true)}
                    />
                  </div>
                )}
              </>
            ) : (
              <ExpensesTabs
                currency={currency}
                filters={filters}
                details={transactions}
                spendingStructure={structure}
                toggleFilter={actions.toggleExpensesFilter}
                postpaid
                showUserNumber
                onChangeActiveTab={this.setActiveTab}
              />
            )}
            {!tooManyTransactions && activeTab === EXPENSES_SECTIONS.STRUCTURE && (
              <TotalSpent
                structure={structure}
                filters={filters}
                currency={currency}
                vat={vat}
                penalties={penalties}
                discountParams={discountParams}
                discountValue={discountValue}
                amount={amount}
              />
            )}
            <SaveDetalizationPopup
              isOpened={isPopupOpened}
              closePopup={() => this.setState({
                isPopupOpened: false,
              })}
              stopDownloading={() => {
                actions.stopDownloading();
                this.setState({ isPopupOpened: false });
              }}
              invoiceNumber={invoiceNumber}
              startDate={startDate}
              endDate={endDate}
              isEmailConfirmed={isEmailConfirmed}
              benNumber={benNumber}
              saveReport={actions.saveDetalization}
              isSaving={isReportSending}
              savingError={reportSendingError}
            />
          </DataBlocks.ContentGroup>
        </DataBlocks.Block>
      </DataBlocks.Container>
    );
  }
}

function mapStateToProps(state) {
  return {
    ...state.detalization.expenses,
    isEmailConfirmed: state.detalization.email.isConfirmed,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({
      fetchInvoiceDetalizationInitialData,
      toggleExpensesFilter,
      saveDetalization,
      stopDownloading,
    }, dispatch),
  };
}

InvoiceDetalization.propTypes = {
  transactions: PropTypes.array,
  filters: PropTypes.array,
  structure: PropTypes.array,
  error: PropTypes.string,
  actions: PropTypes.object,
  location: PropTypes.object,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  status: PropTypes.string,
  dueDate: PropTypes.string,
  unpaidAmount: PropTypes.number,
  currency: PropTypes.string,
  benNumber: PropTypes.number,
  invoiceNumber: PropTypes.string,
  amount: PropTypes.number,
  vat: PropTypes.number,
  penalties: PropTypes.number,
  discountParams: PropTypes.array,
  discountValue: PropTypes.number,
  isEmailConfirmed: PropTypes.bool,
  isReportSending: PropTypes.bool,
  reportSendingError: PropTypes.string,
  tooManyTransactions: PropTypes.bool,
  paymentUrl: PropTypes.string,
  isFetching: PropTypes.bool,
  amount: PropTypes.number,
};

export default connect(mapStateToProps, mapDispatchToProps)(InvoiceDetalization);
